import React, { useEffect, useState, useMemo, useContext } from "react";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Alert from "@material-ui/lab/Alert";
import Container from '@material-ui/core/Container';
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { useForm, useWatch } from "react-hook-form";
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useAuth } from "./../util/auth";
import { makeStyles } from '@material-ui/core/styles';
import { updateProduction, useProductionsByOwner } from "../util/db";
import { US_STATES,COUNTRIES } from "../util/constants";
import MaskedInput from 'react-text-mask';
import { requireAuth } from "./../util/auth";
import { toast } from "react-hot-toast";
import { useTheme } from '@material-ui/core/styles';
import { useRouter } from "./../util/router";
import PhoneMask from "../components/PhoneMask";
import { createInitialLocationFromSignUpAddress } from "../util/createInitialLocationFromSignUpAddress";
import { checkIfProductionNameAndCrewOrExtraKeyExists, createProduction } from "../util/apiHelpers";
import { DateContext } from "../providers/DateProvider";

const useStyles = makeStyles(() => ({
    root: {
      '& input::-webkit-calendar-picker-indicator': {
        filter: 'invert(1)',
        cursor: 'pointer',
      },
    },

  }));

function EnterShowInfoPage(props) {
  const router = useRouter();
  const auth = useAuth();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [pending, setPending] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const { dayStamp } = useContext(DateContext);

  const classes = useStyles();

  useEffect(() => {
    if (auth.user?.userType === 'production') {
      router.replace("/dashboard"); 
      console.log(auth.user?.userType)
    }
  }, [auth.user?.userType]);

  const defaultValues = useMemo(() => {
    return {
      businessName:  "",
      addressLine1:  "",
      addressLine2:  "",
      city:  "",
      state: "",
      postal_code: "",
      country:  "",
        productionName:  "",
      email: auth.user?.email || "",
      phone:  "",
      accessKey:  "",
      accessKeyForExtras: "",
      accessKeyForExtrasExpiry: "",
    };
  }, [auth.user?.email]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
  } = useForm({
    defaultValues,
    mode: "onChange", // this ensures validation runs as user types
  });

  const expiryValue = watch("accessKeyForExtrasExpiry");

  const watchedValues = useWatch({ control });
  const isModified = useMemo(() => {
    const watchedPhone = watchedValues.phone?.replace(/[^0-9]/g, "") || "";
    const defaultPhone = defaultValues.phone?.replace(/[^0-9]/g, "") || "";
    return (
      watchedValues.businessName !== defaultValues.businessName ||
      watchedValues.addressLine1 !== defaultValues.addressLine1 ||
      watchedValues.addressLine2 !== defaultValues.addressLine2 ||
      watchedValues.city !== defaultValues.city ||
      watchedValues.state !== defaultValues.state ||
      watchedValues.postal_code !== defaultValues.postal_code ||
      watchedValues.country !== defaultValues.country ||
      watchedValues.productionName !== defaultValues.productionName ||
      watchedValues.email !== defaultValues.email ||
      watchedValues.accessKey !== defaultValues.accessKey ||
      watchedValues.accessKeyForExtras !== defaultValues.accessKeyForExtras ||
      watchedValues.accessKeyForExtrasExpiry !== defaultValues.accessKeyForExtrasExpiry ||
      watchedPhone !== defaultPhone
    );
  }, [watchedValues, defaultValues]);

  const onSubmit = async (data) => {
    setSubmitError(null);
    setPending(true);
    let isMounted = true;

    // Check if productionName + crewKey or productionName + extraKey already exist
    try {
      const result = await checkIfProductionNameAndCrewOrExtraKeyExists(data.productionName, data.accessKey, data.accessKeyForExtras);
      if (result.exists) {
        const conflictField =
          result.duplicateType === "crewKey"
            ? "crew key"
            : result.duplicateType === "extraKey"
            ? "extra key"
            : "key";

        setSubmitError({
          message: `A production with this name and ${conflictField} already exists. Please choose a different production name or key.`
        });
    
        setPending(false);
        return; // stop the signup process
      } else {
        console.log('No dupes. you can save.');
      }
    
      // continue signup if no duplicate
    } catch (err) {
      console.error("Error checking production duplicate:", err);
      setSubmitError({
        message: `Failed while checking for duplicate production name and key combo.`
      });
      setPending(false);
      return;
    }

    const businessAddress = {
      addressLine1: data.addressLine1,
      addressLine2: data.addressLine2,
      city: data.city,
      state: data.state,
      postal_code: data.postal_code,
      country: data.country,
    };

    const modifiedData = {
      businessName: data.businessName,
      businessAddress, // <-- nested object
      productionName: data.productionName,
      productionAccountEmail: auth.user.email,
      phone: data.phone,
      accessKey: { type: "crew", key: data.accessKey, expiry: false },
      accessKeyForExtras: {
        type: "extra",
        key: data.accessKeyForExtras,
        expiry: data.accessKeyForExtrasExpiry,
      },
    };

      try {
        const productionRef = await createProduction(auth.user.uid, modifiedData);
        await auth.updateProfile({
          businessName: modifiedData.businessName,
          productionName: modifiedData.productionName,
          productionId: productionRef.productionId,
          userType: "production",
        });
      
        // preload first location
        await createInitialLocationFromSignUpAddress(
          productionRef,
          modifiedData,
          dayStamp
        );
      
        if (isMounted) {
          toast.success("Your production has been created");
          router.replace("/dashboard");
        }
      } catch (error) {
        if (isMounted) {
          toast.error(error.message);
        }
      } finally {
        if (isMounted) {
          setPending(false);
        }
      }
    
  }

  return (
    <Container maxWidth="sm" style={{marginBottom: 50}}>
      {auth.user?.email && <><h2>Please enter the following information for your show:</h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                type="text"
                label="Production Company Name"
                name="businessName"
                placeholder="Business Name"
                error={!!errors.businessName}
                helperText={errors.businessName?.message}
                fullWidth
                inputRef={register({ required: "Please enter the business name" })}
              />
            </Grid>
            {/* <Grid item xs={12}>
              <TextField
                variant="outlined"
                type="text"
                label="Production Company Address"
                name="businessAddress"
                placeholder="Business Address"
                error={!!errors.businessAddress}
                helperText={errors.businessAddress?.message}
                fullWidth
                inputRef={register({ required: "Please enter the business Address" })}
              />
            </Grid> */}

            {/* Address Line 1 */}
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                label="Address Line 1"
                name="addressLine1"
                placeholder="Street address"
                error={!!errors.addressLine1}
                helperText={errors.addressLine1?.message}
                fullWidth
                inputRef={register({ required: "Address Line 1 is required" })}
              />
            </Grid>

            {/* Address Line 2 */}
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                label="Address Line 2"
                name="addressLine2"
                placeholder="Apartment, suite, etc. (optional)"
                error={!!errors.addressLine2}
                helperText={errors.addressLine2?.message}
                fullWidth
                inputRef={register()} // optional, no required validation
              />
            </Grid>

            {/* City */}
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                label="City"
                name="city"
                placeholder="City"
                error={!!errors.city}
                helperText={errors.city?.message}
                fullWidth
                inputRef={register({ required: "City is required" })}
              />
            </Grid>

            {/* State */}
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                select
                label="State"
                name="state"
                error={!!errors.state}
                helperText={errors.state?.message}
                fullWidth
                inputRef={register({ required: "State is required" })}
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </TextField>
            </Grid>

            {/* Postal Code */}
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                label="Postal Code"
                name="postal_code"
                placeholder="Postal Code"
                error={!!errors.postal_code}
                helperText={errors.postal_code?.message}
                fullWidth
                inputRef={register({ required: "Postal Code is required" })}
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                select
                label="Country"
                name="country"
                error={!!errors.country}
                helperText={errors.country?.message}
                fullWidth
                inputRef={register({ required: "Country is required" })}
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                variant="outlined"
                type="text"
                label="Production Name for App / Map"
                name="productionName"
                placeholder="Production Name"
                error={!!errors.productionName}
                helperText={errors.productionName?.message}
                fullWidth
                inputProps={{
                  maxLength: 123, // ✅ hard character limit at input level
                }}

                inputRef={register({
                  required: "Please enter the production name",
                  maxLength: {
                    value: 123,
                    message: "Production name cannot exceed 123 characters",
                  },
                })}
                onChange={(e) => {
                  e.target.value = e.target.value.toUpperCase().slice(0, 123);
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                variant="outlined"
                type="text"
                label="Access Key for Crew"
                name="accessKey"
                placeholder="MyShow123"
                error={!!errors.accessKey}
                helperText={errors.accessKey?.message}
                fullWidth
                inputRef={register({ required: "Please enter an access key" })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                type="text"
                label="Access Key for Extras"
                name="accessKeyForExtras"
                placeholder="MyShow123EXTRA"
                error={!!errors.accessKeyForExtras}
                helperText={errors.accessKeyForExtras?.message}
                fullWidth
                inputRef={register({ required: "Please enter an access key for Extras" })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                type="datetime-local"
                name="accessKeyForExtrasExpiry"
                label="Extras Key Expiration"
                error={!expiryValue || !!errors.accessKeyForExtrasExpiry}
                helperText={
                  !expiryValue
                    ? "Please enter extra key expiration"
                    : errors.accessKeyForExtrasExpiry?.message
                }
                fullWidth
                inputRef={register({
                  required: "Please enter extra key expiration",
                })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            )

            <Grid item xs={12}>
            <TextField
            variant="outlined"
            fullWidth
            label="Phone"
            name="phone"
            id="phone"
            error={!!errors.phone}
            helperText={errors.phone?.message}
            InputProps={{
              inputComponent: PhoneMask,
            }}
            InputLabelProps={{
              shrink: true,
            }}
            inputRef={register({
              required: "Please enter a phone number",
              setValueAs: value => value.replace(/\D/g, ""), // strip mask before validation
              validate: (value) => {
                // Remove mask characters and placeholders
                const digits = value.replace(/\D/g, "");
                if (digits.length < 10) {
                  return "Please enter a valid 10-digit phone number";
                }
                return true;
              },
            })}
            />
            </Grid>

            <Grid item xs={12}>
              <TextField
                variant="outlined"
                type="email"
                label="Email"
                name="email"
                placeholder="user@example.com"
                error={!!errors.email}
                helperText={errors.email?.message}
                fullWidth
                disabled
                inputRef={register({ required: "Please enter your email" })}
              />
            </Grid>

            <Grid item xs={12} style={{paddingBottom: 25}}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                type="submit"
                disabled={pending || !isValid}
                fullWidth
              >
                {!pending ? "Next" : <CircularProgress size={28} />}
              </Button>
            </Grid>
            {submitError && <div><Alert severity="error" >{submitError.message}</Alert></div>}

          </Grid>
        </form></>}
        </Container>
  );
}

export default requireAuth(EnterShowInfoPage);
